/**
 * Stop roles → one look everywhere.
 *
 * The list badge, the map pin and the role label all read from here, so stop
 * "C" is the same letter and the same colour in the planner and on the map.
 * One rule, four colours, every pin as easy to see as the next:
 *   start  navy ink      overnight  brand blue (the route line's blue)
 *   via    pale slate    end        red (the only alarm colour)
 *          with a navy rim
 * The role WORDS are all one quiet grey; a night adds a moon icon.
 * The hex values mirror the tp.role-* tokens in tailwind.config.js (the map
 * draws SVG pins, so it needs real colours, not class names).
 */
import { roleForIndex, middleStopNights, type StopRole } from '../../utils/tripRoles';
import { dayHex as tripDayHex } from '../../utils/tripMapRoute';

export type { StopRole };

export const ROLE_HEX: Record<StopRole, string> = {
  start: '#16233a',
  overnight: '#1558b0',
  via: '#5b6b82',
  end: '#c43d2f',
};

/** Tailwind classes for the round badge in the list. */
export const ROLE_BADGE: Record<StopRole, string> = {
  start: 'bg-tp-role-start text-white',
  overnight: 'bg-tp-role-night text-white',
  // A pass-through: a filled pale slate body with a navy rim and letter,
  // the same as its map pin. Solid enough to read on any background.
  via: 'bg-[#dfe5ee] text-tp-ink ring-2 ring-inset ring-tp-ink',
  end: 'bg-tp-role-end text-white',
};

/**
 * Colour for day `day` (1-based, as the list's day breaks count): the same
 * per-day palette the map's route and day chips use (utils/tripDayColors).
 */
export function dayHex(day: number): string {
  return tripDayHex(Math.max(0, Math.floor(day) - 1));
}

/** Tailwind classes for the role word under the stop name. */
export const ROLE_TEXT: Record<StopRole, string> = {
  start: 'text-tp-muted',
  overnight: 'text-tp-muted',
  via: 'text-tp-muted',
  end: 'text-tp-muted',
};

/** A, B, C … Z, then AA, AB … — the letter a stop wears in the list AND on the map. */
export function stopLetter(index: number): string {
  if (index < 0) return '';
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

/** Role of a stop from its position and nights (same rule as utils/tripRoles). */
export function roleAt(
  stops: { kind?: string; nights?: number }[],
  index: number
): StopRole {
  const stop = stops[index];
  const nights = stop ? middleStopNights(stop as never) : 0;
  return roleForIndex(index, stops.length, nights);
}

/**
 * The SVG pin for a stop: a teardrop anchored at its tip, so the letter sits
 * ABOVE the place and the base map's town label stays readable beside it.
 * A pass-through (via) is a size down with a filled pale slate body, a 2px
 * navy rim and a navy letter: it reads at a glance on pale terrain without
 * competing with the places you stop at.
 */
export function stopPinSvg(letter: string, role: StopRole, opts: { selected?: boolean; fill?: string } = {}): string {
  // A day colour (the itinerary's days) overrides the role colour. A
  // pass-through keeps its pale body and wears the colour on its rim.
  const via = role === 'via';
  const tone = opts.fill ?? (via ? ROLE_HEX.start : ROLE_HEX[role]);
  const fill = via ? '#dfe5ee' : tone;
  const stroke = via ? tone : '#ffffff';
  const ink = via ? tone : '#ffffff';
  const strokeW = via ? 2 : 2.5;
  const w = 30;
  const h = 40;
  const ring = opts.selected ? '<circle cx="15" cy="14" r="13.5" fill="none" stroke="#1558b0" stroke-width="2"/>' : '';
  const fontSize = letter.length > 1 ? 10 : 13;
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 30 40">`,
    '<path d="M15 39c-.6 0-1.1-.3-1.4-.8C9 31.6 2 23.4 2 14.5 2 7 7.8 1.5 15 1.5S28 7 28 14.5c0 8.9-7 17.1-11.6 23.7-.3.5-.8.8-1.4.8z" ',
    `fill="${fill}" stroke="${stroke}" stroke-width="${strokeW}"/>`,
    ring,
    `<text x="15" y="18.6" text-anchor="middle" font-family="Work Sans, Arial, sans-serif" font-size="${fontSize}" font-weight="700" fill="${ink}">${letter}</text>`,
    '</svg>',
  ].join('');
}

/** On-map size of a stop pin: pass-throughs are a size down. */
export function stopPinSize(role: StopRole): { w: number; h: number } {
  return role === 'via' ? { w: 23, h: 31 } : { w: 26, h: 35 };
}

export function stopPinDataUrl(letter: string, role: StopRole, opts: { selected?: boolean; fill?: string } = {}): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(stopPinSvg(letter, role, opts))}`;
}
